// src/pages/healthcare/results/testOrderComponents/InitialResultsUpload.tsx

import React from "react";
import { Upload, Input, Select, Button, Space, Checkbox, Row, Col } from "antd";
import { UploadOutlined, DeleteOutlined, FileTextOutlined, FilePdfOutlined, FileImageOutlined } from "@ant-design/icons";

import SectionCard from "../../../../components/shared/SectionCard";
import { downscaleImage } from "../../../../utils/downscaleImage";

const { TextArea } = Input;

interface UploadedFile {
	file: File;
	name: string;
	size: number;
	type: string;
	preview?: string;
}

interface InitialResultsUploadProps {
	uploadedFiles: UploadedFile[];
	resultNotes: string;
	resultStatus: string;
	onFilesChange: (files: UploadedFile[]) => void;
	onNotesChange: (notes: string) => void;
	onStatusChange: (status: string) => void;
	disabled?: boolean;
}

const InitialResultsUpload: React.FC<InitialResultsUploadProps> = ({
	uploadedFiles,
	resultNotes,
	resultStatus,
	onFilesChange,
	onNotesChange,
	onStatusChange,
	disabled = false,
}) => {
	const resultStatusOptions = [
		{ label: "Normal", value: "normal" },
		{ label: "Abnormal", value: "abnormal" },
		{ label: "Critical", value: "critical" },
		{ label: "Pending Review", value: "pending_review" },
	];

	const allowedFileTypes = ["application/pdf", "image/jpeg", "image/jpg", "image/png"];

	// Matches BloodTestAnalysisService::MAX_DOCUMENT_BYTES — a larger file fails after the queue round-trip.
	const maxFileSize = 5 * 1024 * 1024;

	const handleFileSelect = async (file: File) => {
		// Validate file type
		if (!allowedFileTypes.includes(file.type)) {
			alert(`Invalid file type. Please upload a PDF, JPG or PNG.`);
			return false;
		}

		// Downscale before the size check so the limit applies to the file actually sent.
		const processed = await downscaleImage(file);

		// Validate file size
		if (processed.size > maxFileSize) {
			alert(`File size exceeds maximum limit of 5MB. Please compress the file or split it into smaller documents.`);
			return false;
		}

		// Create preview for images
		let preview: string | undefined;
		if (processed.type.startsWith("image/")) {
			preview = URL.createObjectURL(processed);
		}

		onFilesChange([
			...uploadedFiles,
			{
				file: processed,
				name: processed.name,
				size: processed.size,
				type: processed.type,
				preview,
			},
		]);

		return false; // Prevent automatic upload
	};

	const handleFileRemove = (index: number) => {
		const newFiles = [...uploadedFiles];
		// Revoke object URL if it exists
		if (newFiles[index].preview) {
			URL.revokeObjectURL(newFiles[index].preview!);
		}
		newFiles.splice(index, 1);
		onFilesChange(newFiles);
	};

	const formatFileSize = (bytes: number): string => {
		if (bytes < 1024) return `${bytes} B`;
		if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
		return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
	};

	const getFileIcon = (fileType: string) => {
		if (fileType === "application/pdf") return <FilePdfOutlined style={{ fontSize: 20, color: "var(--mg-danger)" }} />;
		if (fileType.startsWith("image/")) return <FileImageOutlined style={{ fontSize: 20, color: "var(--mg-primary)" }} />;
		return <FileTextOutlined style={{ fontSize: 20 }} />;
	};

	return (
		<SectionCard title="Initial Results (Optional)">
			<Space direction="vertical" style={{ width: "100%" }} size="small">
				<div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
					<Upload beforeUpload={handleFileSelect} showUploadList={false} accept=".pdf,.jpg,.jpeg,.png" disabled={disabled}>
						<Button size="small" icon={<UploadOutlined />} disabled={disabled}>
							Select Files
						</Button>
					</Upload>
					<span style={{ fontSize: 11, color: "var(--mg-text-secondary)" }}>PDF, JPG or PNG — max 5MB each. Attached files can be sent for AI analysis.</span>
				</div>

				{uploadedFiles.length > 0 && (
					<Space direction="vertical" style={{ width: "100%" }} size={4}>
						{uploadedFiles.map((uploadedFile, index) => (
							<div
								key={index}
								style={{
									display: "flex",
									alignItems: "center",
									padding: "6px 8px",
									border: "1px solid var(--mg-border)",
									borderRadius: 4,
									backgroundColor: "var(--mg-bg-subtle)",
								}}
							>
								{getFileIcon(uploadedFile.type)}
								<div style={{ flex: 1, marginLeft: 8 }}>
									<div style={{ fontWeight: 500, fontSize: 12 }}>{uploadedFile.name}</div>
									<div style={{ fontSize: 11, color: "var(--mg-text-secondary)" }}>{formatFileSize(uploadedFile.size)}</div>
								</div>
								<Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => handleFileRemove(index)} disabled={disabled} />
							</div>
						))}
					</Space>
				)}

				<Row gutter={[8, 8]}>
					<Col xs={24} md={8}>
						<label style={{ display: "block", marginBottom: 4, fontWeight: 500, fontSize: 11 }}>Result Status</label>
						<Select size="small" value={resultStatus} options={resultStatusOptions} onChange={onStatusChange} style={{ width: "100%" }} disabled={disabled} />
					</Col>
					<Col xs={24} md={16}>
						<label style={{ display: "block", marginBottom: 4, fontWeight: 500, fontSize: 11 }}>Result Notes</label>
						<TextArea
							value={resultNotes}
							onChange={(e) => onNotesChange(e.target.value)}
							placeholder="Add notes about the results (optional)"
							rows={2}
							disabled={disabled}
						/>
					</Col>
				</Row>
			</Space>
		</SectionCard>
	);
};

export default InitialResultsUpload;
